import { Autocomplete, Tooltip } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { useEffect, useLayoutEffect, useState } from "react";
import { useSelector } from 'react-redux';
import setToast from "../../helpers/toaster.helper";
import { BackMobileIcon, CancelIcon } from "../../icon";
import { OrganizationModel } from "../../models/organization.model";
import { mapToPhoneNumber, PhoneNumbergroup, PhoneNumberModel } from "../../models/phone-number.model";
import { ProfileModel } from "../../models/profile.model";
import { UserModel } from "../../models/users.model";
import * as selector from '../../selectors/selectors';
import { UserService } from "../../services/UserService";
import { UtilityService } from "../../services/UtilityService";
import ButtonComponent from "../shared/button/button.component";
import { ToasterTexts, buttonLabels } from "../shared/constants";
import { validateForm } from "../shared/form-validation";
import { ToasterType } from "../shared/toaster/toaster.model";
import { Role, RoleModel } from "../../models/roles.model";
import { PhoneNumberService } from "../../services/PhonenumberService";
import LoaderComponent from "../shared/loaders/animate-loader.component";
import TextBoxAnimationData from "../shared/loaders/textbox-loader.json";
import { LoadPhoneNumberGroups } from "../../actions";
import { useDispatch } from "react-redux";

interface Props {
	isSysAdmin: boolean;
	show: boolean;
	onClose: (isConfim: boolean) => void;
	clearSelection: () => void;
	User?: UserModel;
}

const UserViewComponent = (props: Props) => {
	const dispatch = useDispatch()
	const Organizations: OrganizationModel[] = useSelector(selector.organizations)
	const PhoneNumbersData: PhoneNumberModel[] = useSelector(selector.phoneNumbers)
	const PhoneNumberGroups: PhoneNumbergroup[] = useSelector(selector.phonenumbergroups);
	const Roles: RoleModel[] = useSelector(selector.roles)
	const Profile: ProfileModel = UserService.getCurrentUserFromLocalStorage()
	const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
	const IsDarkMode: boolean = useSelector(selector.isDarkMode)
	const [showNotification, setShowNotification] = useState<boolean>(false)
	const [notificationText, setNotificationText] = useState<string>("")
	const [IsLoading, setIsLoading] = useState<boolean>(false);
	const [PhoneNumbers, setPhoneNumbers] = useState<PhoneNumberModel[]>([]);
	const setTitle = !!props.User?.ID;
	const title = setTitle ? "Edit User" : "Create User";

	const [userdata, setUserdata] = useState<{
		FirstName: string,
		LastName: string,
		Email: string,
		OrganizationID: string;
		PhoneNumberID: string,
		RoleID: number,
		CreatedDateTime: string,
		ID: string,
		ImageReferenceID: string,
		IsDeleted: boolean,
		LastLoggedIn: string,
		Password: string,
		PhoneNumber: string,
		SecondaryPhoneNumbers: { phoneNumberId: string, phoneNumber: string, phoneNumberName: string }[]
		PhoneNumberGroups: any,

	}>(
		{
			...(props.User || {
				FirstName: "",
				LastName: "",
				Email: "",
				OrganizationID: '',
				PhoneNumberID: '',
				RoleID: -1,
				CreatedDateTime: '',
				ID: '',
				ImageReferenceID: '',
				IsDeleted: false,
				LastLoggedIn: '',
				Password: '',
				PhoneNumber: '',
				SecondaryPhoneNumbers: [{ phoneNumberId: "", phoneNumber: "", phoneNumberName: "" }],
				PhoneNumberGroups: []
			}),
		}
	)
	const [errors, setErrors] = useState<{
		FirstName: string,
		LastName: string,
		Email: string,
		PhoneNumberID: string,
		OrganizationID: string,
		Role: string,
		PhoneNumberGroups: string,
	}>({
		FirstName: "",
		LastName: "",
		Email: "",
		PhoneNumberID: "",
		OrganizationID: "",
		Role: "",
		PhoneNumberGroups: "",
	})

	useEffect(() => {
		if (!!userdata.OrganizationID) {
			LoadPhoneNumberGroups(dispatch, userdata.OrganizationID)
		}
	}, [userdata.OrganizationID])

	useEffect(() => {
		if (!props.isSysAdmin) {
			UserService.getCurrentUserFromLocalStorage()
			setUserdata({ ...userdata, OrganizationID: Profile.OrgId });
		}

		if (!!userdata.PhoneNumberID) {
			const PhoneNumber = PhoneNumbers.find(x => x.ID === userdata.PhoneNumberID)?.PhoneNumber
			if (!!PhoneNumber) {
				setUserdata({
					...userdata, PhoneNumber: PhoneNumber, SecondaryPhoneNumbers: props.User.SecondaryPhoneNumbers
				})
			}
		}

	}, [PhoneNumbers])



	useLayoutEffect(() => {
		if (UserService.hasOrgAdminRole() || UserService.hasManagerRole()) {
			setPhoneNumbers(PhoneNumbersData)
		}
	}, [PhoneNumbersData])

	useLayoutEffect(() => {
		if (UserService.hasSysAdminRole() && props.User?.OrganizationID) {
			getPhoneNumbers(props.User.OrganizationID)
		}

		if (UserService.hasSysAdminRole() && !props.User?.OrganizationID && userdata.OrganizationID) {
			getPhoneNumbers(userdata.OrganizationID)
		}

	}, [props.User?.OrganizationID, userdata.OrganizationID])

	const formSubmit = (event: any) => {
		event.preventDefault();

		const obj = userdata.RoleID === Role.MANAGER ?
			{
				FirstName: userdata.FirstName,
				LastName: userdata.LastName,
				Email: userdata.Email,
				OrganizationID: userdata.OrganizationID,
				Role: userdata.RoleID,
				PhoneNumberID: userdata.PhoneNumberID,
				phonenumbergroups: userdata.PhoneNumberGroups
			} : {
				FirstName: userdata.FirstName,
				LastName: userdata.LastName,
				Email: userdata.Email,
				OrganizationID: userdata.OrganizationID,
				Role: userdata.RoleID,
				PhoneNumberID: userdata.PhoneNumberID,
			}

		if (validateForm(obj,
			(Errors) => setErrors({ ...errors, ...Errors, PhoneNumberGroups: Errors.phonenumbergroups }))) {
			setIsLoading(true)
			const group = PhoneNumberGroups.find(x => x.PhoneNumbers?.some(y => y.phoneNumberId === userdata.PhoneNumberID));
			const obj: any = {
				"id": userdata.ID,
				"FirstName": userdata.FirstName,
				"LastName": userdata.LastName,
				"Email": userdata.Email.trim(),
				"OrganizationId": userdata.OrganizationID,
				"PhoneNumberId": userdata.PhoneNumberID,
				"PhoneNumber": userdata.PhoneNumber,
				"RoleId": userdata.RoleID.toString(),
				"isDeleted": userdata.IsDeleted,
				"phoneNumberGroups": userdata.RoleID === Role.MANAGER ? userdata.PhoneNumberGroups?.map(x => ({ id: x.Id, name: x.GroupName })) : group ? [{ id: group.Id, name: group.GroupName }] : [],
				"SecondaryPhoneNumbers": userdata.RoleID === Role.MANAGER ? secondaryPhoneNumberOptions.map(pn => ({ phoneNumberId: pn.ID, phoneNumber: pn.PhoneNumber, phoneNumberName: pn.Name, })) : userdata.SecondaryPhoneNumbers,
			}
			let Name = userdata.FirstName + " " + userdata.LastName
			if (!!userdata.ID) {
				UserService.updateUser(obj)
					.then(() => {
						setIsLoading(false)
						setToast(ToasterTexts.USER_EDIT_SUCCESS.replace('<Name>', Name), ToasterType.SUCCESS);
						props.onClose(true);
					})
					.catch((err) => {
						setToast(err.response.data.message, ToasterType.DANGER)
						setShowNotification(true)
						setNotificationText(err.response.data.message)
						setIsLoading(false)
					});
			} else {
				UserService.createUser(obj)
					.then(() => {
						props.clearSelection()
						setIsLoading(false)
						setToast(ToasterTexts.USER_ADD_SUCCESS.replace('<Name>', Name), ToasterType.SUCCESS)
						props.onClose(true);
					})
					.catch((err) => {
						setToast(err.response.data.message, ToasterType.DANGER)
						setShowNotification(true)
						setIsLoading(false)
						setNotificationText(err.response.data.message)
					});
			}
		}
	}

	const getPhoneNumbers = (id) => {
		PhoneNumberService.getPhoneNumbersByOrgId(id).then((res) => {
			if (res.status === 200) {
				setPhoneNumbers(res.data.map(mapToPhoneNumber));
			}
		}).catch((err) => {
			setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER)
		})
	}


	const handleChange = (name: any, value: any) => {
		validateForm({ [name]: value },
			(Errors) => {
				setErrors({ ...errors, ...Errors })
			})
		setUserdata({ ...userdata, [name]: value });
	};

	const handlephoneGroups = (e, value: any[]) => {
		if (value.length) {
			const SecondaryPhoneNumbers = value.flatMap((group: any) => (group.PhoneNumbers || []).map((pn: any) => PhoneNumbers.find(x => x.ID === pn.phoneNumberId)).filter(Boolean)) || [];
			setUserdata({ ...userdata, PhoneNumberID: "", PhoneNumberGroups: value, SecondaryPhoneNumbers: SecondaryPhoneNumbers.map(x => ({ phoneNumberId: x.ID, phoneNumber: x.PhoneNumber, phoneNumberName: x.Name })) });
			setErrors({ ...errors, PhoneNumberGroups: "" })
		}
		else {
			setUserdata({ ...userdata, PhoneNumberID: "", PhoneNumberGroups: [] })
			setErrors({ ...errors, PhoneNumberGroups: "PhoneNumberGroups cannot be blank" });
		}
	}

	const selectedPhoneNumbers = userdata.SecondaryPhoneNumbers?.map(item => item.phoneNumberId) || [];

	const filteredOptions = PhoneNumbers.filter(x => {
		return x.OrganizationID === userdata.OrganizationID &&
			!!x.Provider &&
			!x.IsDeleted &&
			x.ID !== userdata.PhoneNumberID &&
			!selectedPhoneNumbers.includes(x.ID);
	});

	const secondaryPhoneNumberOptions = userdata.RoleID === Role.USER && userdata.PhoneNumberID ? (() => {
		const group = PhoneNumberGroups.find(x => x.PhoneNumbers?.some(y => y.phoneNumberId === userdata.PhoneNumberID));
		let availableNumbers: PhoneNumberModel[] = [];

		if (group) {
			const otherPhoneNumberIds = group?.PhoneNumbers?.filter(pn => pn.phoneNumberId !== userdata.PhoneNumberID).map(pn => pn.phoneNumberId) || [];
			availableNumbers = PhoneNumbers.filter(pn => otherPhoneNumberIds.includes(pn.ID));
		}
		else {
			const groupPhoneNumberIds = PhoneNumberGroups.flatMap(g => g.PhoneNumbers?.map(pn => pn.phoneNumberId) || []);
			availableNumbers = PhoneNumbers.filter(pn => !groupPhoneNumberIds.includes(pn.ID) && pn.ID !== userdata.PhoneNumberID);
		}
		return availableNumbers;
	})()
		: userdata.RoleID === Role.MANAGER && userdata.PhoneNumberID ? (() => {
			const managerGroupIds = userdata.PhoneNumberGroups.map(g => g.Id);
			const phoneIds = PhoneNumberGroups.filter(g => managerGroupIds.includes(g.Id)).flatMap(g => g.PhoneNumbers?.map(p => p.phoneNumberId) || []).filter(id => id !== userdata.PhoneNumberID);
			const availableNumbers = PhoneNumbers.filter(pn => phoneIds.includes(pn.ID));

			return availableNumbers;
		})()
			: filteredOptions;



	// filtering phonenumbers from selected groups for manager role
	const groupPhoneNumberIds: string[] = PhoneNumberGroups.filter(x => userdata.PhoneNumberGroups.map(y => y.Id).includes(x.Id)).flatMap((x: any) => (x.PhoneNumbers || []).map((x: any) => x.phoneNumberId)) || []
	const filteredPhoneNumbers: PhoneNumberModel[] = PhoneNumbers?.filter(pn => groupPhoneNumberIds.includes(pn.ID));

	// filtering phonenumbers from selected groups for user role
	const phonenumbers = () => {
		const user = UserService.getCurrentUserFromLocalStorage();
		const ids = [user?.PhoneNumberID, ...(user?.SecondaryPhoneNumbers?.map((x: any) => x.phoneNumberId) || [])];
		return PhoneNumbers.filter(x => ids.includes(x.ID));
	}

	return (
		<div className="w-fill">
			{
				IsMobileDevice ?
					<div className="paper-background o-x-hidden w-fill h-v-100">
						<Grid item xs={12}>
							<div className='box-shadow-display-background-shadow d-flex p-2'>
								{IsMobileDevice && <span><BackMobileIcon className={"c-pointer pr-2"} onClick={() => props.onClose(false)} /></span>}
								<span className={`h1 my-auto color-add-details-title mr-auto`}>{title}</span>
							</div>
						</Grid>
						<form
							noValidate
							autoComplete="no"
							onSubmit={formSubmit}
							className="m-2"
						>
							<Grid container direction="column" spacing={3} className="mt-2">
								<Grid item xs={12}>
									<TextField
										name="FirstName"
										placeholder="First Name"
										label="First Name"
										variant="outlined"
										size="small"
										required
										fullWidth
										value={userdata.FirstName}
										onChange={(e) => handleChange(e.target.name, e.target.value)
										}
										InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
										autoComplete='no'
									/>
									<FormHelperText className="h5 color-primary-danger t-align-right">
										{errors.FirstName}
									</FormHelperText>
								</Grid>
								<Grid item xs={12}>
									<TextField
										placeholder="Last Name"
										name="LastName"
										label="Last Name"
										variant="outlined"
										size="small"
										fullWidth
										required
										value={userdata.LastName}
										onChange={(e) =>
											handleChange(e.target.name, e.target.value)
										}
										InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
										autoComplete='no'
									/>
									<FormHelperText className="h5 color-primary-danger t-align-right">
										{errors.LastName}
									</FormHelperText>
								</Grid>
								<Grid item xs={12}>
									<TextField
										fullWidth
										label="Email"
										name="Email"
										placeholder="Email"
										size="small"
										required
										variant="outlined"
										value={userdata.Email}
										onChange={(e) =>
											handleChange(e.target.name, e.target.value)
										}
										InputProps={{ classes: { input: 'h2 color-text-field-font' } }}
										autoComplete='no'
									/>
									<FormHelperText className="h5 color-primary-danger t-align-right">
										{errors.Email}
									</FormHelperText>
								</Grid>
								{props.isSysAdmin ? (
									<Grid item xs={12}>
										<Autocomplete
											size="small"
											disabled={!!props.User?.ID}
											options={Organizations.filter(x => x.Name != null)}
											getOptionLabel={(option) => option?.Name}
											classes={{ input: 'h2 color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
											onChange={(event: any, value: any | null) => {
												if (value) {
													setUserdata({ ...userdata, OrganizationID: value.ID, PhoneNumber: "", PhoneNumberID: "" });
													setErrors({ ...errors, OrganizationID: "" })
												}
												else {
													setUserdata({ ...userdata, OrganizationID: "" });
													setErrors({ ...errors, OrganizationID: "Organization cannot be blank" });
												}
											}}
											value={Organizations.find(x => x.ID === userdata.OrganizationID)}
											renderOption={(defaultprops, option: any) => (
												<li {...defaultprops} className={"c-pointer h2 color-primary-dark p-1 d-flex justify-between w-p-90"}>
													{option?.Name}
												</li>
											)}
											renderInput={(params) => <TextField {...params} label="Organization" name="OrganizationId" value={userdata.OrganizationID} required variant="outlined"
											/>}
										/>
										<FormHelperText className="h5 color-primary-danger t-align-right">
											{errors.OrganizationID}
										</FormHelperText>
									</Grid>) : <> </>}
								<Grid item >
									<Autocomplete
										size="small"
										classes={{ input: 'h2 color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
										options={Roles.filter(x => x.ID !== 1 && x.ID >= parseInt(Profile.RoleID))}
										getOptionLabel={(option) => option?.Name}
										onChange={(event: any, value: any | null) => {
											if (value) {
												setUserdata({ ...userdata, RoleID: value.ID });
												setErrors({ ...errors, Role: "" })
											}
											else {
												setUserdata({ ...userdata, RoleID: -1 });
												setErrors({ ...errors, Role: "Role cannot be blank" })
											}
										}}
										value={Roles.find(x => x.ID === userdata.RoleID)}
										renderOption={(defaultprops, option: any) => (
											<li {...defaultprops} className={"h2 p-2 d-flex justify-between w-p-90"}>
												{option.Name}
											</li>
										)}
										renderInput={(params) => <TextField {...params} label="Role" name="roleId" required variant="outlined" />}
									/>
									<FormHelperText className="h5 color-primary-danger t-align-right">
										{errors.Role}
									</FormHelperText>
								</Grid>
								<Grid item xs={12}>
									{!!userdata.OrganizationID ? (!!PhoneNumbers.length || !props.User?.ID) ?
										<Autocomplete
											size="small"
											className={""}
											classes={{ input: 'h2 color-text-field-font', paper: 'box-shadow-none dialogue-shadow' }}
											options={PhoneNumbers.filter(x => {
												if (props.isSysAdmin)
													return x.OrganizationID === userdata.OrganizationID && !!x.Provider && !x.IsDeleted
												else return true
											})}

											getOptionLabel={(option) => UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber) + ` (${option?.Name})`}
											value={PhoneNumbers.find(x => x.ID === userdata.PhoneNumberID)}
											onChange={(event: any, value: PhoneNumberModel | null) => {
												if (value) {
													const updatedSecondaryPhoneNumbers = userdata.SecondaryPhoneNumbers?.filter(item => item.phoneNumberId !== value.ID);
													setUserdata({ ...userdata, PhoneNumber: value.PhoneNumber, PhoneNumberID: value.ID, SecondaryPhoneNumbers: updatedSecondaryPhoneNumbers });
													setErrors({ ...errors, PhoneNumberID: "" })
												}
												else {
													setUserdata({ ...userdata, PhoneNumber: "", PhoneNumberID: "" });
													setErrors({ ...errors, PhoneNumberID: "Phone Number cannot be blank" })
												}
											}}
											renderOption={(defaultprops, option: any) => (
												<li {...defaultprops} className={"c-pointer h2 d-flex justify-between w-p-90 px-2"}>
													<div>{option?.Name.length > 15 ? UtilityService.trimMessage(option?.Name, 15) : option?.Name} </div>
													<div> {UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber)}</div>
												</li >
											)}
											renderInput={(params) => <TextField {...params} label="Select Phone Number" name="phoneNumberId" required variant="outlined" />}
										/>
										: <LoaderComponent LoaderAnimation={TextBoxAnimationData} /> : <> </>}
									<FormHelperText className="h5 color-primary-danger t-align-right">
										{errors.PhoneNumberID}
									</FormHelperText>
								</Grid>
								<Grid item xs={12}>
									{!!userdata.OrganizationID && userdata.RoleID === Role.USER ? !!PhoneNumbers.length ? (
										<Autocomplete
											multiple
											size="small"
											limitTags={1}
											className={"bg-color-secondary c-pointer"}
											options={secondaryPhoneNumberOptions}
											renderInput={(params) =>
												<TextField {...params}
													label="Select Switchable Numbers"
													name="secondaryPhoneNumberIds"
													variant="outlined"
												/>}
											disableCloseOnSelect={true}
											getOptionLabel={(option) => UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber) + ` (${option?.Name})`}
											value={PhoneNumbers.filter(option => userdata.SecondaryPhoneNumbers?.some(x => x.phoneNumber === option.PhoneNumber))}
											onChange={(event: any, value: PhoneNumberModel[] | null) => {
												if (value) {
													setUserdata({ ...userdata, SecondaryPhoneNumbers: value.map(x => ({ phoneNumberId: x.ID, phoneNumber: x.PhoneNumber, phoneNumberName: x.Name })) });
												} else {
													setUserdata({ ...userdata, SecondaryPhoneNumbers: [{ phoneNumberId: "", phoneNumber: "", phoneNumberName: "" }] });
												}
											}}
											renderOption={(defaultprops, option: any) => (
												<li {...defaultprops} className={"c-pointer h2 d-flex justify-between w-p-90 px-2"}>
													<div >{option?.Name} </div>
													<div> {UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber)}</div>
												</li>
											)}
										/>
									) :
										<LoaderComponent LoaderAnimation={TextBoxAnimationData} />
										: <></>
									}
								</Grid>
							</Grid>
						</form>
						<div className="d-flex px-2 mt-auto">
							<ButtonComponent OnClick={() => props.onClose(false)} ButtonLabel={buttonLabels.CANCEL} ClassName={"mr-1 ml-auto"} Variant={"secondary-btn-small"} />
							<ButtonComponent OnClick={formSubmit} Loading={IsLoading} ButtonLabel={buttonLabels.SAVE} ClassName={"px-4 "} Variant={"primary-btn-small"} />
						</div>
					</div> :
					<Dialog
						maxWidth="sm"
						fullWidth={true}
						BackdropProps={{ style: { backgroundColor: "transparent" } }}
						open={props.show}
						// onClose={() => props.onClose(false)}
						aria-labelledby="alert-dialog-title"
						aria-describedby="alert-dialog-description"
						classes={{ paper: `w-66 br-2 ${IsDarkMode ? "" : "dialogue-shadow"}` }}
						disableEscapeKeyDown
					>
						<DialogTitle id="alert-dialog-title" className={`dialog-header ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-primary-background"}`}>
							<div className='d-flex'>
								<span className={`h2-regular ${IsDarkMode ? "" : "color-display-title"}`}>{title}</span>
								<Tooltip title='Close' placement='bottom-start'>
									<CancelIcon className={"c-pointer ml-auto"} onClick={() => props.onClose(false)} />
								</Tooltip>
							</div>
						</DialogTitle>
						<DialogContent className={` p-3 o-x-hidden ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-primary-background"} `}>
							<form
								noValidate
								autoComplete="no"
							>
								<Grid container direction="row" spacing={2}>
									<Grid item xs={6} >
										<TextField autoFocus
											name="FirstName"
											placeholder="First Name"
											label="First Name"
											variant="outlined"
											size="small"
											required
											fullWidth
											value={userdata.FirstName}
											onChange={(e) => handleChange(e.target.name, e.target.value)
											}
											InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}

											autoComplete='no'
										/>
										<FormHelperText className="h6-regular color-primary-danger t-align-right">
											{errors.FirstName}
										</FormHelperText>
									</Grid>
									<Grid item xs={6} >
										<TextField
											placeholder="Last Name"
											name="LastName"
											label="Last Name"
											variant="outlined"
											size="small"
											fullWidth
											required
											value={userdata.LastName}
											onChange={(e) =>
												handleChange(e.target.name, e.target.value)
											}
											InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
											autoComplete='no'
										/>
										<FormHelperText className="h6-regular color-primary-danger t-align-right">
											{errors.LastName}
										</FormHelperText>
									</Grid>
								</Grid>
								<Grid item xs={12} className="mt-2">
									<TextField
										fullWidth
										label="Email"
										name="Email"
										placeholder="Email"
										size="small"
										required
										variant="outlined"
										value={userdata.Email}

										onChange={(e) =>
											handleChange(e.target.name, e.target.value)
										}
										InputProps={{ classes: { input: 'h4-regular color-text-field-font' } }}
										className="mt-1"
										autoComplete='no'

									/>
									<FormHelperText className="h6-regular color-primary-danger t-align-right">
										{errors.Email}
									</FormHelperText>
								</Grid>
								{props.isSysAdmin ? (
									<Grid item xs={12} className="mt-3">
										<Autocomplete
											size="small"
											classes={{ input: 'h4-regular color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
											disabled={!!props.User?.ID}
											options={Organizations.filter(x => x.Name != null && x.Name !== "Admin" && !x.IsDeleted)}
											getOptionLabel={(option) => option?.Name}
											onChange={(event: any, value: any | null) => {
												if (value) {
													setUserdata({ ...userdata, OrganizationID: value.ID, PhoneNumber: "", PhoneNumberID: "" });
													setErrors({ ...errors, OrganizationID: "" })
												}
												else {
													setUserdata({ ...userdata, OrganizationID: "" });
													setErrors({ ...errors, OrganizationID: "Organization cannot be blank" });
												}
											}}
											value={Organizations.find(x => x.ID === userdata.OrganizationID)}
											renderOption={(defaultprops, option: any) => (
												<li {...defaultprops}>
													<span className={"c-pointer h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-92"}>
														{option?.Name}
													</span>
												</li>
											)}
											renderInput={(params) => <TextField {...params} label="Organization" name="organizationId" value={userdata.OrganizationID} required variant="outlined"
											/>}
										/>
										<FormHelperText className="h6-regular color-primary-danger t-align-right">
											{errors.OrganizationID}
										</FormHelperText>
									</Grid>) : null}
								<Grid item xs={12} className="mt-3">
									<Autocomplete
										size="small"
										classes={{ input: 'h4-regular color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
										options={UserService.hasManagerRole() ? Roles.filter(x => x.ID === Role.USER) : Roles.filter(x => x.ID !== 1)}
										getOptionLabel={(option) => option?.Name}
										onChange={(event: any, value: any | null) => {
											if (value) {
												setUserdata({ ...userdata, RoleID: value.ID, PhoneNumberGroups: [] });
												setErrors({ ...errors, Role: "" })
											}
											else {
												setUserdata({ ...userdata, RoleID: -1 });
												setErrors({ ...errors, Role: "Role cannot be blank" })
											}
										}}
										value={Roles.find(x => x.ID === userdata.RoleID)}
										renderOption={(defaultprops, option: any) => (
											<li {...defaultprops}>
												<span className={" c-pointer h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-92"}>
													{option?.Name}
												</span>
											</li>
										)}
										renderInput={(params) => <TextField {...params} label="Role" name="roleId" required variant="outlined" />}
									/>
									<FormHelperText className="h6-regular color-primary-danger t-align-right">
										{errors.Role}
									</FormHelperText>
								</Grid>
								<Grid item xs={12} className="mt-3">{(userdata.RoleID == Role.MANAGER) &&
									<Autocomplete
										multiple
										size="small"
										classes={{ input: 'h4-regular color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
										options={PhoneNumberGroups.filter(x => !x.IsDeleted && !userdata.PhoneNumberGroups?.some((y: any) => y.Id === x.Id))}
										getOptionLabel={(option) => option?.GroupName || ""}
										onChange={(e: any, value: any) => handlephoneGroups(e, value)
										}
										value={userdata.PhoneNumberGroups}
										renderOption={(defaultprops, option: any) => (
											<li {...defaultprops}>
												<span className={"c-pointer h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-92"}>
													{option?.GroupName}
												</span>
											</li>
										)}
										renderInput={(params) =>
											<TextField {...params}
												label="Line Groups"
												name="PhoneNumberGroups"
												required variant="outlined"
											/>}
										disabled={(UserService.getLoggedInUserRole() === Role.MANAGER && UserService.getCurrentUserId() === userdata.ID)}
									/>}
									<FormHelperText className="h6-regular color-primary-danger t-align-right">
										{errors.PhoneNumberGroups}
									</FormHelperText>
								</Grid>
								<Grid item xs={12} className="mt-3">
									{!!userdata.OrganizationID ? (!!PhoneNumbers.length || !props.User?.ID) ?
										<Autocomplete
											size="small"
											key={userdata.OrganizationID}
											classes={{ input: 'h4-regular color-text-field-font', paper: 'bg-color-menu-bg box-shadow-none dialogue-shadow' }}
											options={(userdata.RoleID === Role.MANAGER) ? filteredPhoneNumbers : (userdata.RoleID === Role.USER && UserService.hasManagerRole()) ? phonenumbers() : PhoneNumbers}
											getOptionLabel={(option) => UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber) + ` (${option?.Name})`}
											value={userdata.PhoneNumberID ? PhoneNumbers.find(x => x.ID === userdata.PhoneNumberID) : null}
											onChange={(event: any, value: PhoneNumberModel | null) => {
												if (value) {

													let updatedSecondaryPhoneNumbers = userdata.SecondaryPhoneNumbers?.filter(item => item.phoneNumberId !== value.ID);
													setUserdata({ ...userdata, PhoneNumber: value.PhoneNumber, PhoneNumberID: value.ID, SecondaryPhoneNumbers: updatedSecondaryPhoneNumbers });
													setErrors({ ...errors, PhoneNumberID: "" })
												}
												else {
													setUserdata({ ...userdata, PhoneNumber: "", PhoneNumberID: "" });
													setErrors({ ...errors, PhoneNumberID: "Phone Number cannot be blank" })
												}
											}}
											renderOption={(defaultprops, option: any) => (
												<li {...defaultprops}>
													<span className={" c-pointer h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-92"}>
														<div >{option?.Name} </div>
														<div> {UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber)}</div>
													</span>
												</li>
											)}
											renderInput={(params) => <TextField {...params} label="Select Phone Number" name="phoneNumberId" required variant="outlined" />}
										/>
										: <LoaderComponent LoaderAnimation={TextBoxAnimationData} /> : <></>}
									{!!userdata.OrganizationID && <FormHelperText className="h6-regular color-primary-danger t-align-right">
										{errors.PhoneNumberID}
									</FormHelperText>}
								</Grid>
								<Grid item xs={12} className="mt-3">
									{!!userdata.OrganizationID && (userdata.RoleID === Role.USER) ? !!PhoneNumbers.length ? (
										<Autocomplete
											multiple
											size="small"
											limitTags={1}
											className={"bg-color-secondary c-pointer"}
											options={secondaryPhoneNumberOptions}
											renderInput={(params) =>
												<TextField {...params}
													label="Select Switchable Numbers"
													name="secondaryPhoneNumberIds"
													variant="outlined"
												/>}
											disableCloseOnSelect={true}
											getOptionLabel={(option) => UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber) + ` (${option?.Name})`}
											value={PhoneNumbers.filter(option => userdata.SecondaryPhoneNumbers?.some(x => x.phoneNumber === option.PhoneNumber))}
											onChange={(event: any, value: PhoneNumberModel[] | null) => {
												if (value) {
													setUserdata({ ...userdata, SecondaryPhoneNumbers: value.map(x => ({ phoneNumberId: x.ID, phoneNumber: x.PhoneNumber, phoneNumberName: x.Name })) });
												} else {
													setUserdata({ ...userdata, SecondaryPhoneNumbers: [{ phoneNumberId: "", phoneNumber: "", phoneNumberName: "" }] });
												}
											}}
											renderOption={(defaultprops, option: any) => (
												<li {...defaultprops}>
													<span className={"c-pointer h4-regular color-menu-list-font-color p-2 d-flex justify-between w-p-92"}>
														<div >{option?.Name} </div>
														<div> {UtilityService.maskPhoneNumberOnLength(option?.PhoneNumber)}</div>
													</span>
												</li>
											)}
										/>
									) : (PhoneNumbers.length != 0 &&
										<LoaderComponent LoaderAnimation={TextBoxAnimationData} />)
										: <></>
									}
								</Grid>
								<div className="d-flex float-right mt-3 mb-2">
									<ButtonComponent OnClick={() => props.onClose(false)} ButtonLabel={buttonLabels.CANCEL} ClassName={"mr-1"} Variant={"secondary-btn-small"} />
									<ButtonComponent OnClick={formSubmit} Loading={IsLoading} ButtonLabel={buttonLabels.SAVE} ClassName={"px-4"} Variant={"primary-btn-small"} />
								</div>
							</form>
						</DialogContent >
					</Dialog >}
		</div >
	)
}

export default UserViewComponent;